export interface SurveyResponseInput {
  rating: number;
  feedback?: string;
  surveyMonth?: Date;
}

export interface SurveyResponseView {
  id: number;
  rating: number;
  feedback?: string;
  submittedAt: Date;
  surveyMonth: Date;
  companyId?: number;
  userId?: string;
  branchId?: number;
  isArchived: boolean;
}

export interface SurveyStatsView {
  month: Date;
  totalResponses: number;
  promoters: number;
  passives: number;
  detractors: number;
  enpsScore: number;
  averageRating: number;
}

export interface RecognitionInput {
  recipientId: string;
  message: string;
  values?: string[];
  visibilityType?: FeedbackVisibilityType;
}

export interface RecognitionView {
  id: number;
  senderId: string;
  senderName: string;
  senderProfilePictureUrl?: string;
  recipientId: string;
  recipientName: string;
  recipientProfilePictureUrl?: string;
  message: string;
  values: string[];
  timestamp: string;
  companyId?: number;
  visibilityType: FeedbackVisibilityType;
  isPublic: boolean;
}

export interface CompanyRecognitionValueView {
  id: number;
  name: string;
  description?: string;
  companyId: number;
  createdAt: Date;
  isActive: boolean;
}

export interface CompanyRecognitionValueInput {
  id?: number;
  name: string;
  description?: string;
}

export interface RecognitionRecipientView {
  recipientId: string;
  recipientFirstName: string;
  recipientLastName: string;
  recipientProfilePictureUrl?: string;
}

// Reflection Input Models
export interface MonthlyReflectionInput {
  periodId: number;
  responseText: string;
  responseStatus: string; // "draft" or "submitted"
}

export interface ManagerReflectionResponseInput {
  userReflectionId: number;
  message: string;
  encouragementType?: string;
}

// Reflection View Models
export interface UserReflectionView {
  id: number;
  userId: string;
  userName?: string;
  periodId: number;
  promptId: number;
  responseText: string;
  responseStatus: string;
  submittedAt?: Date;
  createdOn: Date;
  updatedOn: Date;
  companyId?: number;
  branchId?: number;
  departmentId?: number;
  isArchived: boolean;

  // Additional fields from mapping
  promptTitle?: string;
  promptCategory?: string;
  managerResponse?: ManagerReflectionResponseView;
}

export interface ManagerReflectionResponseView {
  id: number;
  message: string;
  encouragementType?: string;
  isRead: boolean;
  readAt?: Date;
  userReflectionId: number;
  managerId: string;
  managerName?: string;
  managerImageUrl?: string;
  createdOn: Date;
  updatedOn: Date;

  // Navigation collections
  actionItems: ReflectionActionItemView[];
  resources: ReflectionResourceView[];
}

export interface ReflectionActionItemView {
  id: number;
  itemText: string;
  displayOrder?: number;
  isCompleted: boolean;
  completedAt?: Date;
}

export interface ReflectionResourceView {
  id: number;
  title: string;
  resourceUrl: string;
  description?: string;
  displayOrder?: number;
}

export interface ReflectionContributionStatusView {
  submissionDeadline: Date;
  isDeadlinePassed: boolean;
  draftCount: number;
  submittedCount: number;
  totalPrompts: number;
  hasContributed: boolean;
}

export interface ReflectionPeriodView {
  id: number;
  periodMonth: number;
  periodYear: number;
  startDate: Date;
  endDate: Date;
  submissionDeadline: Date;
  isActive: boolean;
  isArchived: boolean;
}

export interface ReflectionPrompt {
  id: number;
  title: string;
  category: string;
  displayOrder: number;
  isActive: boolean;
  isArchived: boolean;
}

export interface GoalMilestoneInput {
  title: string;
  dueDate: string;
  completed: boolean;
}

export interface GoalMilestoneView extends GoalMilestoneInput {
  id: number;
  goalId: string;
  completedAt?: Date;
  createdOn: Date;
  updatedOn: Date;
  isArchived: boolean;
}

export interface GoalInput {
  title: string;
  description: string;
  level: 'company' | 'team' | 'individual';
  status: 'on-track' | 'at-risk' | 'completed' | 'overdue';
  progress: number;
  ownerId: string;
  department: string;
  startDate: string;
  dueDate: string;
  parentId?: string;
  milestones?: GoalMilestoneInput[];
}

export interface GoalView {
  id: string;
  title: string;
  description: string;
  level: 'company' | 'team' | 'individual';
  status: 'on-track' | 'at-risk' | 'completed' | 'overdue';
  progress: number;
  ownerId: string;
  ownerName: string;
  department: string;
  startDate: string;
  dueDate: string;
  parentId?: string;
  parentTitle?: string;
  createdOn: Date;
  updatedOn: Date;
  companyId?: number;
  branchId?: number;
  isArchived: boolean;

  // Navigation properties
  milestones: GoalMilestoneView[];
  subGoals: GoalView[];
  linkedMetrics?: GoalMetricView[];
}

export interface GoalMetricView {
  id: number;
  goalId: string;
  metricType: string;
  currentValue: number;
  targetValue: number;
  unit: string;
  createdOn: Date;
  updatedOn: Date;
  isArchived: boolean;
}

export interface GoalAlignmentView {
  goal: GoalView;
  parentGoal?: GoalView;
  childGoals: GoalView[];
  allConnections: GoalView[];
}

export interface GoalProgressUpdateInput {
  goalId: string;
  progress: number;
  status: 'on-track' | 'at-risk' | 'completed' | 'overdue';
  notes?: string;
}

export interface GoalProgressUpdateView {
  id: number;
  goalId: string;
  userId: string;
  userName: string;
  previousProgress: number;
  newProgress: number;
  previousStatus: string;
  newStatus: string;
  notes?: string;
  timestamp: Date;
}

export interface GoalFilterOptions {
  search?: string;
  department?: string;
  user?: string;
  dueDate?: string;
  status?: string[];
  levels?: string[];
  parentId?: string;
  startDate?: string;
  endDate?: string;
}

export interface GoalStatsView {
  totalGoals: number;
  onTrackCount: number;
  atRiskCount: number;
  completedCount: number;
  overdueCount: number;
  averageProgress: number;
  byLevel: {
    company: GoalLevelStats;
    team: GoalLevelStats;
    individual: GoalLevelStats;
  };
  byDepartment: {
    [department: string]: GoalLevelStats;
  };
}

export interface GoalLevelStats {
  total: number;
  onTrack: number;
  atRisk: number;
  completed: number;
  overdue: number;
  averageProgress: number;
}

export interface GoalCommentInput {
  goalId: string;
  comment: string;
}

export interface GoalCommentView {
  id: number;
  goalId: string;
  userId: string;
  userName: string;
  userProfilePictureUrl?: string;
  comment: string;
  timestamp: Date;
  isEdited: boolean;
  editedAt?: Date;
}

export interface GoalReminderView {
  id: number;
  goalId: string;
  userId: string;
  reminderDate: Date;
  reminderType: 'weekly' | 'monthly' | 'quarterly' | 'custom';
  isActive: boolean;
  createdOn: Date;
  updatedOn: Date;
}

export interface GoalAttachmentView {
  id: number;
  goalId: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByName: string;
  uploadedAt: Date;
}

export interface TeamHubPostInput {
  content: string;
  postType: string;
  department?: string;
  tags?: string[];
  attachmentUrls?: string[];
}

export interface TeamHubPostView {
  id: number;
  authorId: string;
  authorName: string;
  authorAvatar?: string;  // Changed from authorProfilePictureUrl to match backend
  content: string;
  postType: string;
  department?: string;
  tags: string[];
  attachmentUrls: string[];
  postedAt: Date;
  updatedAt?: Date;
  likeCount: number;
  commentCount: number;
  isLikedByUser: boolean;
  companyId?: number;
  branchId?: number;
  isArchived: boolean;
  comments?: TeamHubPostCommentView[];
}

export interface TeamHubPostCommentInput {
  postId: number;
  content: string;
  parentCommentId?: number;
}

export interface TeamHubPostCommentView {
  id: number;
  postId: number;
  authorId: string;
  authorName: string;
  authorAvatar?: string;  // Changed from authorProfilePictureUrl to match backend
  content: string;
  commentedAt: Date;
  updatedAt?: Date;
  likeCount: number;
  isLikedByUser: boolean;
  parentCommentId?: number;
  isArchived: boolean;
  replies?: TeamHubPostCommentView[];
}

export interface TeamHubFilterOptions {
  department?: string;
  postType?: string;
  search?: string;
  tags?: string[];
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

// Add these interfaces to your existing models
export interface DashboardRecognitionView {
  id: number;
  senderId?: string;
  recipientId?: string;
  senderName: string;
  recipientName: string;
  avatarUrl?: string;
  senderImageUrl?: string;
  recipientImageUrl?: string;
  message: string;
  values: string[];
  points: number;
  date: string; // ISO date string
}

export interface RecognitionStatisticsView {
  receivedCount: number;
  givenCount: number;
  totalPointsReceived: number;
  totalPointsGiven: number;
  valuesBreakdown: Record<string, number>;
  monthlyTrend: Record<string, number>;
}

export interface DashboardSummaryView {
  title: string;
  value: string;
  icon: string;
  color: string;
  trend: TrendView;
  description: string;
}

export interface TrendView {
  type: 'up' | 'down' | 'stable';
  value: string;
}

export interface FiltersDto {
  dateRange?: string;
  values?: string;
  category?: string;
  type?: string;
}

export interface GoalDashboardStatsView {
  // Company Stats
  companyTotalGoals: number;
  companyCompletedGoals: number;
  companyOnTrackGoals: number;
  companyAtRiskGoals: number;
  companyOverdueGoals: number;
  companyAverageProgress: number;
  companyCompletionRate: number;
  companyGoalsByLevel: { [key: string]: number };
  companyGoalsByStatus: { [key: string]: number };

  // User Personal Stats
  userTotalGoals: number;
  userCompletedGoals: number;
  userOnTrackGoals: number;
  userAtRiskGoals: number;
  userOverdueGoals: number;
  userAverageProgress: number;
  userCompletionRate: number;
  userGoalsByLevel: { [key: string]: number };
  userGoalsByStatus: { [key: string]: number };

  // Assigned Goals Stats
  assignedTotalGoals: number;
  assignedCompletedGoals: number;
  assignedOnTrackGoals: number;
  assignedAtRiskGoals: number;
  assignedOverdueGoals: number;
  assignedAverageProgress: number;
  assignedCompletionRate: number;

  // Milestone Stats
  totalMilestones: number;
  completedMilestones: number;

  // Recent Activity
  recentGoalsCreated: number;
  recentGoalsCompleted: number;

  // Engagement Metrics
  engagementScore: number;
  activeGoalStreak: number;

  // Metadata
  analysisDate: string;
  companyId: number;
}

export interface GoalProgressIndicator {
  id: number;
  title: string;
  subtitle: string;
  icon: string;
  value: number;
  period: string;
  description: string;
  trend: string;
  change: string;
  category: string;
}
// Interfaces matching the backend ViewModels
export interface SummaryMetricView {
  title: string;
  value: string;
  change: string;
  changeType: string;
  icon: string;
  description: string;
  trend: string;
  timePeriod?: string;
  badge?: string;
  tooltip?: string;
}

export interface DepartmentOptionView {
  id: number;
  name: string;
}

export interface RecognitionDataView {
  name: string;
  recognitions: number;
}

export interface EngagementTrendView {
  month: string;
  goalAlignment: number;
  reflectionRate: number;
  enpsScore: number;
}

export interface GoalProgressView {
  name: string;
  value: number;
  total: number;
}

export interface FeedbackThemeView {
  id: number;
  title: string;
  description: string;
  sentiment: string;
  category: string;
  frequency: string;
  mentions: number;
  lastSeen: string;
  tags: string[];
}

export interface InsightsDashboardView {
  summaryMetrics: SummaryMetricView[];
  recognitionData: RecognitionDataView[];
  engagementTrends: EngagementTrendView[];
  goalProgress: GoalProgressView[];
  feedbackThemes: FeedbackThemeView[];
  lastUpdated: string;
}

export interface InsightsFilterView {
  dateRange: string;
  department: string;
  startDate?: string;
  endDate?: string;
}

export interface Q12DimensionDataView {
  id: string;
  score: number;
  trend: number;
  hidden: boolean;
  respondentCount: number;
  isPlaceholder?: boolean;
}

export interface Q12DepartmentColumnView {
  departmentId: number;
  departmentName: string;
  dimensions: Q12DimensionDataView[];
}

export interface Q12HeatmapView {
  company: Q12DimensionDataView[];
  department: Q12DimensionDataView[];
  departmentColumns: Q12DepartmentColumnView[];
}

export interface EngagementDistributionView {
  engaged: number;
  notEngaged: number;
  activelyDisengaged: number;
  total: number;
}

export interface EngagementRiskView {
  title: string;
  scope: string;
  confidence: 'High' | 'Medium' | 'Low';
  dimensions: string[];
  intervention: string;
}

export interface CorrelationDataPointView {
  category: string;
  engagement: number;
  outcome: number;
}

export interface EngagementImpactView {
  goalCompletion: CorrelationDataPointView[];
  absenteeism: CorrelationDataPointView[];
  participation: CorrelationDataPointView[];
}

export interface GoalAchievementProgressView {
  goalGetterProgress: number;
  goalGetterTarget: number;
  goalGetterUnlocked: boolean;
  consistentAchieverProgress: number;
  consistentAchieverTarget: number;
  consistentAchieverUnlocked: boolean;
}

// Q12 Insights Drill-Down & Trends
export interface Q12SubFactor {
  name: string;
  score: number;
  weight: number;
  actionUrl?: string;
}

export interface Q12FactorBreakdown {
  dimensionId: string;
  dimensionName: string;
  overallScore: number | null;
  subFactors: Q12SubFactor[];
  trendHistory: { period: string; score: number | null }[];
  suggestedActions: { title: string; description: string; actionUrl: string; icon: string }[];
}

export interface Q12TrendDataView {
  period: string;
  dimensions: Record<string, number | null>;
}

// Feedback Enums
export enum FeedbackVisibilityType {
  Private = 0,
  Public = 1
}

export enum FeedbackType {
  Coaching = 1,
  Concern = 2
}

// Feedback Input Models
export interface FeedbackInput {
  receivedById: string;
  comment: string;
  feedbackType: FeedbackType;
  // Feedback is always private - no visibility option
}

// Feedback View Models
export interface FeedbackView {
  id: number;
  givenById: string;
  givenByName: string;
  receivedById: string;
  receivedByName: string;
  comment: string;
  createdOn: string;
  visibilityType: FeedbackVisibilityType;
  isPublic: boolean;
  feedbackType: FeedbackType;
}

// Recognition & Feedback Config
export interface RecognitionFeedbackConfigView {
  allowPublicRecognitionFeedback: boolean;
}

export interface RecognitionFeedbackConfigInput {
  allowPublicRecognitionFeedback: boolean;
}

// eNPS Analytics
export interface ENPSAnalyticsFilterView {
  dateRange?: string;
  department?: string;
  location?: string;
}

export interface ENPSOverviewView {
  currentScore: number;
  previousScore: number;
  change: number;
  trend: 'up' | 'down' | 'stable';
  participationRate: number;
  previousParticipationRate: number;
  totalResponses: number;
  eligibleEmployees: number;
  promoters: number;
  passives: number;
  detractors: number;
  promoterPercentage: number;
  passivePercentage: number;
  detractorPercentage: number;
  scoreCategory: 'Strong' | 'Moderate' | 'Developing';
  lastUpdated: string | null;
  insufficientData: boolean;
}

export interface ENPSTrendPointView {
  month: string;
  enpsScore: number;
  participationRate: number;
  totalResponses: number;
  insufficientData: boolean;
}

export interface ENPSSegmentOptionView {
  id: string;
  name: string;
  responseCount: number;
  hasSufficientData: boolean;
}

export interface ENPSSegmentOptionsView {
  departments: ENPSSegmentOptionView[];
  locations: ENPSSegmentOptionView[];
}

// eNPS Theme Signals
export interface ENPSThemeSignalView {
  id: number;
  theme: string;
  mentionPercentage: number;
  lastCyclePercentage: number;
  changePercent: number;
  direction: 'up' | 'down' | 'stable';
  sentiment: 'positive' | 'constructive' | 'neutral';
  mentionCount: number;
  suppressed: boolean;
}

// eNPS Focus Areas
export interface ENPSFocusAreaView {
  id: number;
  title: string;
  rationale?: string;
  reviewWindow?: string;
  status: 'ongoing' | 'reviewed' | 'archived';
  startDate: string;
  reviewedDate?: string;
  outcome?: string;
  linkedTheme?: string;
}

export interface ENPSFocusAreaInput {
  title: string;
  rationale?: string;
  reviewWindow?: string;
  linkedTheme?: string;
}

export interface ENPSFocusAreaUpdateInput {
  title?: string;
  rationale?: string;
  reviewWindow?: string;
  status?: string;
  outcome?: string;
}

// eNPS Learnings
export interface ENPSLearningView {
  id: number;
  text: string;
  displayOrder: number;
}

export interface ENPSLearningInput {
  text: string;
  displayOrder: number;
}

export interface ENPSLearningBulkInput {
  learnings: ENPSLearningInput[];
}

export interface ENPSExportInput {
  format: 'csv' | 'pdf';
  dateRange: string;
  department: string;
  location: string;
}